import { useState, useRef, useCallback, useEffect } from 'react';
import { addNotification } from '../utils/notificationHistory';
import type { FeedMessage } from '../services/wsjFeedService';

const SETTINGS_KEY = 'wsj_feed_notification_settings';

interface NotificationSettings {
  soundEnabled: boolean;
  desktopEnabled: boolean;
}

interface UseWsjFeedNotificationsReturn {
  settings: NotificationSettings;
  isAudioPrimed: boolean;
  notificationPermission: NotificationPermission | 'unsupported';
  toggleSound: () => Promise<void>;
  toggleDesktop: () => Promise<void>;
  notifyNewMessage: (msg: FeedMessage) => void;
}

function loadSettings(): NotificationSettings {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* use defaults */ }
  return { soundEnabled: false, desktopEnabled: false };
}

function saveSettings(settings: NotificationSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch { /* silently fail */ }
}

// Play a 300Hz sine beep — distinct from Wiseguy's 440-880Hz
async function playBeep(audioContext: AudioContext): Promise<void> {
  if (audioContext.state === 'suspended') {
    try {
      await audioContext.resume();
    } catch {
      return;
    }
  }

  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.frequency.value = 300;
  oscillator.type = 'sine';

  gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.1);
}

// Silent keep-alive oscillator to prevent AudioContext from suspending
function startAudioKeepAlive(audioContext: AudioContext): () => void {
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  gainNode.gain.setValueAtTime(0, audioContext.currentTime);
  oscillator.frequency.value = 1;
  oscillator.start();

  return () => {
    try { oscillator.stop(); } catch { /* already stopped */ }
  };
}

export function useWsjFeedNotifications(): UseWsjFeedNotificationsReturn {
  const [settings, setSettings] = useState<NotificationSettings>(loadSettings);
  const [isAudioPrimed, setIsAudioPrimed] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | 'unsupported'>(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );

  const audioContextRef = useRef<AudioContext | null>(null);
  const keepAliveCleanupRef = useRef<(() => void) | null>(null);

  // Persist settings on change
  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      keepAliveCleanupRef.current?.();
      audioContextRef.current?.close();
    };
  }, []);

  // Prime audio system (must be called from user gesture)
  const primeAudio = useCallback(async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }
    if (!keepAliveCleanupRef.current) {
      keepAliveCleanupRef.current = startAudioKeepAlive(audioContextRef.current);
    }
    setIsAudioPrimed(true);
    playBeep(audioContextRef.current);
  }, []);

  const toggleSound = useCallback(async () => {
    if (!settings.soundEnabled) {
      await primeAudio();
    }
    setSettings(prev => ({ ...prev, soundEnabled: !prev.soundEnabled }));
  }, [settings.soundEnabled, primeAudio]);

  const toggleDesktop = useCallback(async () => {
    if (!settings.desktopEnabled) {
      // Request permission when enabling
      if (typeof Notification !== 'undefined' && Notification.permission !== 'granted' && Notification.permission !== 'denied') {
        const permission = await Notification.requestPermission();
        setNotificationPermission(permission);
        if (permission !== 'granted') return;
      }
    }
    setSettings(prev => ({ ...prev, desktopEnabled: !prev.desktopEnabled }));
  }, [settings.desktopEnabled]);

  const notifyNewMessage = useCallback((msg: FeedMessage) => {
    // Sound notification
    if (settings.soundEnabled && audioContextRef.current) {
      playBeep(audioContextRef.current);
    }

    // Desktop notification
    if (settings.desktopEnabled && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      const title = msg.source_type === 'wiseguy_alert' && msg.metadata?.symbol
        ? `WSJ Feed: ${msg.metadata.symbol} Alert`
        : 'WSJ Feed: New Message';
      const body = msg.content.slice(0, 100);

      addNotification({ title, body, source: 'wsj_feed' });

      const notification = new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: `wsj-feed-${msg.id}`,
        requireInteraction: false,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      setTimeout(() => notification.close(), 5000);
    }
  }, [settings.soundEnabled, settings.desktopEnabled]);

  return {
    settings,
    isAudioPrimed,
    notificationPermission,
    toggleSound,
    toggleDesktop,
    notifyNewMessage,
  };
}
